import { ConfirmAction } from "@atlas/composite";
import { Form, FormInputField, FormTextarea } from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { IHubPackageVisibility } from "@enums";
import type { IHubPackage } from "@models";
import { useMemo } from "react";
import { useForm } from "react-hook-form";

export interface IEditPackageDialogProps {
  open: boolean;
  variant: "edit" | "create";
  pkg: IHubPackage | null;
  onOpenChange: (open: boolean) => void;
  isSubmitting: boolean;
  onSave: (values: {
    label: string;
    description: string;
    visibility: IHubPackageVisibility;
  }) => void;
}

interface IEditPackageFormValues {
  label: string;
  description: string;
  visibility: IHubPackageVisibility;
}

const EMPTY_VALUES: IEditPackageFormValues = {
  label: "",
  description: "",
  visibility: IHubPackageVisibility.Private,
};

export function EditPackageDialog({
  open,
  variant,
  pkg,
  onOpenChange,
  isSubmitting,
  onSave,
}: IEditPackageDialogProps) {
  const { t } = useTranslation();

  const computedValues = useMemo<IEditPackageFormValues>(() => {
    if (variant === "edit" && pkg) {
      return {
        label: pkg.label ?? "",
        description: pkg.description ?? "",
        visibility: pkg.visibility ?? IHubPackageVisibility.Private,
      };
    }
    return EMPTY_VALUES;
  }, [variant, pkg]);

  const form = useForm<IEditPackageFormValues>({
    defaultValues: EMPTY_VALUES,
    values: open ? computedValues : undefined,
  });
  const { control, handleSubmit, reset } = form;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      reset(computedValues);
    }
    onOpenChange(next);
  };

  const onFormSubmit = handleSubmit((values) => {
    onSave({
      label: values.label.trim(),
      description: values.description.trim(),
      visibility: values.visibility,
    });
  });

  const labelRequiredMessage = t("hub.package_manager.edit_label_required");

  return (
    <ConfirmAction
      open={open}
      onOpenChange={handleOpenChange}
      title={
        variant === "edit"
          ? t("hub.package_manager.edit_title")
          : t("hub.package_manager.create_title")
      }
      description={
        <Form {...form}>
          <form
            id="edit-package-form"
            onSubmit={(e) => {
              e.preventDefault();
              onFormSubmit(e);
            }}
            className="flex w-full flex-col space-y-4 pt-4 text-left">
            <FormInputField
              control={control}
              name="label"
              label={t("hub.package_manager.column_name", {
                defaultValue: "Name",
              })}
              description={t("hub.package_manager.edit_label_hint")}
              disabled={isSubmitting}
              required
              validate={(v) =>
                String(v ?? "").trim().length > 0 || labelRequiredMessage
              }
            />
            <FormTextarea
              control={control}
              name="description"
              label={t("hub.catalog_subscribe.solution_description")}
              description={t("hub.package_manager.edit_description_hint")}
              disabled={isSubmitting}
              rows={3}
            />
          </form>
        </Form>
      }
      cancelText={t("hub.administer.cancel")}
      confirmText={
        variant === "edit"
          ? t("hub.administer.save")
          : t("hub.catalog_manager.breadcrumb_contribute")
      }
      onConfirm={() => {
        const formElement = document.getElementById(
          "edit-package-form",
        ) as HTMLFormElement;
        if (formElement) {
          formElement.requestSubmit();
        }
      }}
    />
  );
}
